import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RequireAuth } from '../components/RequireAuth';
import { Layout } from '../components/Layout';
import { LegalDocumentText } from '../components/LegalDocumentText';
import { OutrisSiteFooter } from '../components/OutrisSiteFooter';
import { markLegalDocumentViewed } from '../legal/acceptance';
import { PRIVACY_POLICY, TERMS_OF_USE } from '../legal/legalDocuments';
import { hasActiveSession } from '../utils/session';

export function LegalDocumentPage({
    kind,
    embedded = false,
}: {
    kind: 'privacy' | 'terms';
    embedded?: boolean;
}) {
    useEffect(() => {
        markLegalDocumentViewed(kind);
    }, [kind]);

    const body = kind === 'privacy' ? PRIVACY_POLICY : TERMS_OF_USE;
    const otherTo = kind === 'privacy' ? '/terms' : '/privacy';
    const otherLabel = kind === 'privacy' ? 'Terms of Use' : 'Privacy Policy';

    if (embedded) {
        return (
            <div className="px-4 py-6 sm:px-8" data-testid={`legal-${kind}`}>
                <div className="mb-4 flex flex-wrap items-center gap-4 text-sm">
                    <Link to="/" className="text-primary hover:underline">
                        ← Dashboard
                    </Link>
                    <Link to={otherTo} className="text-muted-foreground hover:text-foreground hover:underline">
                        {otherLabel}
                    </Link>
                </div>
                <LegalDocumentText body={body} />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col bg-brand-page dark:bg-background text-foreground" data-testid={`legal-${kind}`}>
            <header className="border-b border-border bg-card">
                <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4 text-sm">
                    <Link to="/login" className="text-muted-foreground hover:text-foreground">
                        ← Sign in
                    </Link>
                    <div className="flex gap-4">
                        <Link to={otherTo} className="text-primary hover:underline">
                            {otherLabel}
                        </Link>
                        <Link to="/login" className="text-muted-foreground hover:text-foreground">
                            CryptoFlow
                        </Link>
                    </div>
                </div>
            </header>
            <LegalDocumentText body={body} />
            <OutrisSiteFooter />
        </div>
    );
}

/** Logged-in officers keep the app chrome; guests keep the standalone policy page. */
export function LegalPolicyRoute({ kind }: { kind: 'privacy' | 'terms' }) {
    if (hasActiveSession()) {
        return (
            <RequireAuth requireLegal={false}>
                <Layout>
                    <LegalDocumentPage kind={kind} embedded />
                </Layout>
            </RequireAuth>
        );
    }
    return <LegalDocumentPage kind={kind} />;
}

export function PrivacyPolicyPage() {
    return <LegalPolicyRoute kind="privacy" />;
}

export function TermsOfUsePage() {
    return <LegalPolicyRoute kind="terms" />;
}
